/**
 * 퇴원 회원 복구 전 비교 창 (2026-10-28 대표 지시 "덮어쓰는 걸 물어보거나 안내를 하는 게 좋을 것 같은데")
 *
 * 회원 추가가 퇴원한 기존 회원과 겹치면(409 DELETED_MEMBER_EXISTS) 이 창이 뜬다.
 * 기존 정보와 새로 입력한 정보가 다른 항목만 나란히 보여 주고, 항목마다
 * [기존 유지] / [새 값으로 변경] 을 고른다(기본 = 기존 유지).
 * - 새 값이 비어 있으면 그 항목은 안 보인다(기존 유지).
 * - 새 번호가 덜 입력됐으면(010·0 등) [새 값으로 변경]을 고를 수 없다.
 * 서버는 apply_fields 에 든 항목만 바꾼다 — 고르지 않은 항목은 절대 안 바뀐다.
 */
import React, { useMemo, useState } from 'react';
import {
  memberRepository,
} from '../../repositories';
import type { DeletedMemberExistsError, RestoreField } from '../../repositories/MemberRepository';

const digitsOf = (v: unknown) => String(v ?? '').replace(/[^0-9]/g, '');

/** 휴대폰 번호 형식 — 서버 utils/phone_utils.is_valid_mobile 과 같은 기준 */
export const isValidMobile = (v: unknown) => {
  const d = digitsOf(v);
  return d.length >= 10 && d.length <= 11 && d.startsWith('01');
};

/** 가운데 가리기 — 010-****-0743. 형식이 아니면 입력된 그대로(덜 입력된 번호를 원장이 알아보게) */
export const maskPhone = (v: unknown) => {
  const d = digitsOf(v);
  if (!d) return '';
  if (d.length === 11) return `${d.slice(0, 3)}-****-${d.slice(7)}`;
  if (d.length === 10) return `${d.slice(0, 3)}-***-${d.slice(6)}`;
  return String(v).trim();
};

const LABELS: Record<RestoreField, string> = {
  name: '이름',
  school: '학교',
  grade: '학년',
  gender: '성별',
  parent_phone_1: '학부모 연락처 1',
  parent_phone_2: '학부모 연락처 2',
};
const FIELDS: RestoreField[] = ['name', 'school', 'grade', 'gender', 'parent_phone_1', 'parent_phone_2'];

const isPhoneField = (f: RestoreField) => f === 'parent_phone_1' || f === 'parent_phone_2';
const genderLabel = (v: unknown) => (v === 'male' ? '남' : v === 'female' ? '여' : v === 'other' ? '기타' : String(v ?? ''));
const show = (f: RestoreField, v: unknown) => {
  if (v === null || v === undefined || String(v).trim() === '') return '(없음)';
  if (isPhoneField(f)) return maskPhone(v);
  if (f === 'gender') return genderLabel(v);
  return String(v);
};
const same = (f: RestoreField, a: unknown, b: unknown) =>
  isPhoneField(f) ? digitsOf(a) === digitsOf(b) : String(a ?? '').trim() === String(b ?? '').trim();

interface Props {
  conflict: DeletedMemberExistsError;
  onCancel: () => void;
  onRestored: () => void;
}

const RestoreCompareModal: React.FC<Props> = ({ conflict, onCancel, onRestored }) => {
  const { deletedMember, memberData } = conflict;
  const current = deletedMember.current || {};

  const rows = useMemo(
    () =>
      FIELDS.flatMap((f) => {
        const newVal = (memberData as any)[f];
        if (newVal === null || newVal === undefined || String(newVal).trim() === '') return []; // 비었으면 기존 유지 — 안 보임
        if (same(f, current[f], newVal)) return [];
        const blocked = isPhoneField(f) && !isValidMobile(newVal);
        return [{ field: f, oldVal: current[f], newVal, blocked }];
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [conflict]
  );

  const [choice, setChoice] = useState<Partial<Record<RestoreField, 'keep' | 'new'>>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleRestore = async () => {
    setSaving(true);
    setError('');
    try {
      const applyFields = rows.filter((r) => !r.blocked && choice[r.field] === 'new').map((r) => r.field);
      await memberRepository.restoreMember(deletedMember.id, memberData, applyFields);
      onRestored();
    } catch (e: any) {
      setError(e?.message || '복구하지 못했습니다.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="restore-compare-modal">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">이전에 퇴원한 회원이 있습니다</h3>
          <p className="mt-1 text-sm text-gray-600">
            {deletedMember.name} · {maskPhone(deletedMember.phone)}
            {deletedMember.user_number ? ` · 회원번호 ${deletedMember.user_number}` : ''}
          </p>
          <p className="mt-2 text-sm text-gray-700">이 회원을 다시 재원생으로 복구합니다.</p>
        </div>

        <div className="px-6 py-4">
          {rows.length === 0 ? (
            <p className="text-sm text-gray-700">새로 입력한 정보가 기존 정보와 같습니다. 기존 정보 그대로 복구합니다.</p>
          ) : (
            <>
              <p className="text-sm text-gray-700 mb-3">
                기존 정보와 새로 입력한 정보가 <b>다른 항목</b>입니다. 항목마다 어느 쪽으로 할지 골라 주세요.
                <span className="text-gray-500"> (고르지 않으면 기존 정보 그대로)</span>
              </p>
              <table className="w-full text-sm border">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium text-gray-600 w-32">항목</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">기존 정보</th>
                    <th className="px-3 py-2 text-left font-medium text-gray-600">새로 입력한 정보</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const sel = r.blocked ? 'keep' : choice[r.field] || 'keep';
                    return (
                      <tr key={r.field} className="border-t align-top">
                        <td className="px-3 py-2 text-gray-700">{LABELS[r.field]}</td>
                        <td className="px-3 py-2">
                          <label className={`flex items-center gap-2 cursor-pointer rounded px-2 py-1 ${sel === 'keep' ? 'bg-blue-50 ring-1 ring-blue-400' : ''}`}>
                            <input
                              type="radio"
                              name={`restore-${r.field}`}
                              checked={sel === 'keep'}
                              onChange={() => setChoice((c) => ({ ...c, [r.field]: 'keep' }))}
                            />
                            <span>
                              <span className="block text-gray-900 whitespace-nowrap">{show(r.field, r.oldVal)}</span>
                              <span className="block text-xs text-gray-500">기존 유지</span>
                            </span>
                          </label>
                        </td>
                        <td className="px-3 py-2">
                          <label
                            className={`flex items-center gap-2 rounded px-2 py-1 ${r.blocked ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'} ${sel === 'new' ? 'bg-blue-50 ring-1 ring-blue-400' : ''}`}
                          >
                            <input
                              type="radio"
                              name={`restore-${r.field}`}
                              checked={sel === 'new'}
                              disabled={r.blocked}
                              onChange={() => setChoice((c) => ({ ...c, [r.field]: 'new' }))}
                            />
                            <span>
                              <span className="block text-gray-900 whitespace-nowrap">{show(r.field, r.newVal)}</span>
                              <span className="block text-xs text-gray-500">새 값으로 변경</span>
                            </span>
                          </label>
                          {r.blocked && (
                            <p className="mt-1 text-xs text-red-600">
                              새로 입력한 번호가 끝까지 입력되지 않았습니다. 기존 번호를 유지합니다.
                            </p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {rows.some((r) => isPhoneField(r.field) && choice[r.field] === 'new' && !r.blocked) && (
                <p className="mt-3 text-sm text-amber-700">바꾼 학부모 번호로 입·퇴실 알림이 갑니다.</p>
              )}
            </>
          )}
          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
        </div>

        <div className="px-6 py-4 border-t flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={saving}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
          >
            취소
          </button>
          <button
            onClick={handleRestore}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
          >
            {saving ? '복구 중…' : '복구하기'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RestoreCompareModal;
